// Phase 2 Round 5 extraction.
//
// 2026-04-29 (mig 034 + Phase 1C-A): submit paths now flow through the
// parent-aware offline RPC queue (`useOfflineRpcSubmit('add_feed_batch')`).
// The RPC `submit_add_feed_batch` (mig 034) owns idempotency via the
// parent's client_submission_id and writes child rows atomically with
// daily_submission_id linkage. Children carry NULL csid — parent owns
// dedup. See supabase-migrations/034_daily_submissions.sql for the RPC contract
// and 221_s2_function_allowlists.sql for its role allowlist.
//
// Submit lifecycle:
//   - synced  : RPC succeeded online; "Feed logged!" UI as before.
//   - queued  : network/transient failure; submission persisted in
//               IndexedDB and replays automatically on online event /
//               60s tick / next mount. "Saved on this device" copy.
//   - stuck   : 3 retries exhausted; surfaces in StuckSubmissionsModal.
//
// FuelSupply (Phase 1B canary) and the future fan-out forms (WeighIns,
// PigDailys) use the FLAT-INSERT queue path (`useOfflineSubmit`) — Add
// Feed is on the RPC path because it produces N child rows from one
// submission and atomicity matters.
import React from 'react';
import {useQueuedRpcRecovery} from './useQueuedRpcRecovery.js';
import {recoveryText} from '../lib/offlineSubmissionRecovery.js';
import {formatBroilerBatchLabel} from '../lib/broilerBatchMeta.js';
import {todayCentralISO} from '../lib/dateUtils.js';
import {useOfflineRpcSubmit} from '../lib/useOfflineRpcSubmit.js';
import {withSavePrefix} from '../lib/saveErrorText.js';
import PlannerIcon from '../components/PlannerIcon.jsx';
import {ANIMAL_ICON_KEYS} from '../lib/plannerIcons.js';
import {programDotStyle} from '../lib/programColors.js';
import type {AddFeedConfigData, AddFeedInputRow} from '../app/data/routeDataCatalog.js';
import type {BroilerBatchMetaEntry} from '../lib/broilerBatchMeta.js';
import type {RpcSubmitState} from '../lib/useOfflineRpcSubmit.js';
import type {SessionSubmitter} from './webformsRoutes.js';
import StuckSubmissionsModal, {payloadMember, payloadText} from './StuckSubmissionsModal.jsx';
import LockedSubmitter from './LockedSubmitter.jsx';
import {useAddFeedDraft} from './useAddFeedDraft.js';

// A non-null, non-array object. Every webform_config blob this form reads is
// operator-edited jsonb, so each hop into one is proved rather than assumed.
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// `catch (e)` binds `unknown` under strict TypeScript. This is the file's own
// `e && e.message ? e.message : String(e)` idiom with that narrowing written out.
function errorMessage(e: unknown): string {
  if (typeof e === 'object' && e !== null && 'message' in e && typeof e.message === 'string' && e.message) {
    return e.message;
  }
  return String(e);
}

// One extra batch/group row on the broiler/pig/layer form. renderFeedGroup
// edits exactly these three members, which is what lets the computed
// assignment below stay a string-into-string write.
type ExtraFeedGroup = {batchLabel: string; feedType: string; feedLbs: string};

type CattleFeedRow = {feedId: string; qty: string; isCreep: boolean};
type SheepFeedRow = {feedId: string; qty: string};

// The dropdown option lists come out of webform_config as jsonb, where an entry
// may be a plain name or an object carrying one. This is the existing
// `typeof b === 'object' ? b.name || b.label || b.value || '' : b` read, with
// the narrowing that makes it type-safe.
function batchOptionName(option: unknown): string {
  if (typeof option === 'object' && option !== null) {
    const named = payloadMember(option, 'name') ?? payloadMember(option, 'label') ?? payloadMember(option, 'value');
    return named === null || named === undefined ? '' : String(named);
  }
  return option === null || option === undefined ? '' : String(option);
}

// The app-side authenticated bridge owns the seven table reads and hands their
// values in as plain props. Keeping this form unaware of query scope/auth state
// preserves the protected-webform boundary: src/webforms never chooses a
// public or authenticated scope for farm data.
export type AddFeedResourceProps = {
  feedInputs?: AddFeedInputRow[];
  addFeedConfig?: AddFeedConfigData | null;
};

type AddFeedWebformProps = {sessionSubmitter?: SessionSubmitter} & AddFeedResourceProps;

const EMPTY_UNKNOWN_ARRAY: unknown[] = [];
const EMPTY_HOUSING_BATCH_MAP: Record<string, string> = {};
const EMPTY_WEBFORM_SETTINGS: Record<string, never> = {};

const AddFeedWebform = ({sessionSubmitter, feedInputs = [], addFeedConfig = null}: AddFeedWebformProps) => {
  // Lane 1 CP1: on the authenticated path the submitter is the signed-in user,
  // locked — shown regardless of the admin team_member field toggle so the
  // session identity is always captured.
  const lockedName = sessionSubmitter?.name || '';
  const submitterLocked = !!lockedName;
  // Each lookup keeps the initial fallback it had when the form owned the
  // effects. Feed inputs and the config bundle resolve independently, and an
  // individual missing config row stays local to that option rather than
  // becoming a form-level error.
  const cattleFeedInputs = feedInputs.filter(function (f) {
    return f.status !== 'inactive';
  });
  const wfCfg = addFeedConfig?.fullConfig || null;
  const wfSettings = addFeedConfig?.webformSettings || EMPTY_WEBFORM_SETTINGS;
  const housingBatchMap = (addFeedConfig?.housingBatchMap || EMPTY_HOUSING_BATCH_MAP) as Record<string, string>;
  const broilerGroups =
    Array.isArray(addFeedConfig?.broilerGroups) && addFeedConfig.broilerGroups.length > 0
      ? addFeedConfig.broilerGroups
      : EMPTY_UNKNOWN_ARRAY;
  const broilerMeta = Array.isArray(addFeedConfig?.broilerBatchMeta)
    ? (addFeedConfig.broilerBatchMeta as BroilerBatchMetaEntry[])
    : [];
  const pigGroups =
    Array.isArray(addFeedConfig?.activeGroups) && addFeedConfig.activeGroups.length > 0
      ? addFeedConfig.activeGroups
      : EMPTY_UNKNOWN_ARRAY;
  const {layerGroupNames, layerBatchIdMap} = React.useMemo(() => {
    if (!wfCfg) return {layerGroupNames: EMPTY_UNKNOWN_ARRAY, layerBatchIdMap: EMPTY_HOUSING_BATCH_MAP};
    const layerGroupsRaw: unknown[] = isRecord(wfCfg) && Array.isArray(wfCfg.layerGroups) ? wfCfg.layerGroups : [];
    const lgAll = layerGroupsRaw.filter(function (g) {
      return isRecord(g) && g.status === 'active';
    });
    const lg = lgAll.map(function (g) {
      return payloadMember(g, 'name') || g;
    });
    const idMap: Record<string, string> = {};
    lgAll.forEach(function (g) {
      const id = payloadMember(g, 'id');
      const name = payloadMember(g, 'name');
      if (id && name) idMap[String(name)] = String(id);
    });
    Object.entries<unknown>(housingBatchMap).forEach(function (e) {
      const batchName = e[1];
      const batch = lgAll.find(function (g) {
        return payloadMember(g, 'name') === batchName;
      });
      const batchId = payloadMember(batch, 'id');
      if (batchId) idMap[e[0]] = String(batchId);
    });
    return {layerGroupNames: lg.length > 0 ? lg : EMPTY_UNKNOWN_ARRAY, layerBatchIdMap: idMap};
  }, [wfCfg, housingBatchMap]);

  const [program, setProgram] = React.useState('');
  const [date, setDate] = React.useState('');
  const [batchLabel, setBatchLabel] = React.useState('');
  const [feedType, setFeedType] = React.useState('');
  const [feedLbs, setFeedLbs] = React.useState('');
  const [teamMember, setTeamMember] = React.useState(sessionSubmitter?.name || '');
  const [extraGroups, setExtraGroups] = React.useState<ExtraFeedGroup[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState('');
  // 'none' | 'synced' | 'queued' — replaces the prior boolean `done`.
  // synced = RPC landed online. queued = persisted in IDB; will replay.
  const [doneState, setDoneState] = React.useState<RpcSubmitState | 'none'>('none');
  const [stuckOpen, setStuckOpen] = React.useState(false);

  const {submit, stuckRows, retryStuck, discardStuck} = useOfflineRpcSubmit('add_feed_batch');

  // Open the stuck modal automatically the first time we observe stuck rows
  // (mount or after a failed sync pass). Operator can close it; we don't
  // re-open on every render. Mirrors FuelSupplyWebform's pattern.
  const initialStuckShownRef = React.useRef(false);
  React.useEffect(() => {
    if (stuckRows.length > 0 && !initialStuckShownRef.current) {
      initialStuckShownRef.current = true;
      setStuckOpen(true);
    }
  }, [stuckRows.length]);
  // Cattle quick-log state (parallel to the poultry/pig fields above)
  const [cattleHerd, setCattleHerd] = React.useState('');
  const [cattleRows, setCattleRows] = React.useState<CattleFeedRow[]>([{feedId: '', qty: '', isCreep: false}]);
  // Sheep quick-log state (cattle-parity shape after migration 012)
  const [sheepFlock, setSheepFlock] = React.useState('');
  const [sheepRows, setSheepRows] = React.useState<SheepFeedRow[]>([{feedId: '', qty: ''}]);

  // The form's answers, as the queued-report recovery and the unfinished
  // draft both capture and restore them.
  const answers = {
    program,
    date,
    batchLabel,
    feedType,
    feedLbs,
    extraGroups,
    cattleHerd,
    cattleRows,
    sheepFlock,
    sheepRows,
  };
  function restoreAnswers(d: typeof answers) {
    setProgram(d.program);
    setDate(d.date);
    setBatchLabel(d.batchLabel);
    setFeedType(d.feedType);
    setFeedLbs(d.feedLbs);
    setExtraGroups(d.extraGroups);
    setCattleHerd(d.cattleHerd);
    setCattleRows(d.cattleRows);
    setSheepFlock(d.sheepFlock);
    setSheepRows(d.sheepRows);
  }
  const recovery = useQueuedRpcRecovery('add_feed_batch', {
    capture: () => ({...answers, doneState}),
    restore: (d) => {
      restoreAnswers(d);
      setDoneState(d.doneState);
    },
    apply: (loaded) => {
      const p = loaded.payload;
      if (!['pig', 'broiler', 'layer', 'cattle', 'sheep'].includes(recoveryText(p.program)))
        throw new Error('This program needs manual recovery. The report was retained.');
      const extras = Array.isArray(p.extraGroups) ? p.extraGroups.filter(isRecord) : [];
      const cattle = Array.isArray(p.cattleFeedsJ) ? p.cattleFeedsJ.filter(isRecord) : [];
      const sheep = Array.isArray(p.sheepFeedsJ) ? p.sheepFeedsJ.filter(isRecord) : [];
      setProgram(recoveryText(p.program));
      setDate(recoveryText(p.date));
      setBatchLabel(recoveryText(p.batchLabel));
      setFeedType(recoveryText(p.feedType));
      setFeedLbs(recoveryText(p.feedLbs));
      setExtraGroups(
        extras.map((x) => ({
          batchLabel: recoveryText(x.batchLabel),
          feedType: recoveryText(x.feedType),
          feedLbs: recoveryText(x.feedLbs),
        })),
      );
      setCattleHerd(recoveryText(p.cattleHerd));
      setCattleRows(
        cattle.map((x) => ({feedId: recoveryText(x.feed_input_id), qty: recoveryText(x.qty), isCreep: !!x.is_creep})),
      );
      setSheepFlock(recoveryText(p.sheepFlock));
      setSheepRows(sheep.map((x) => ({feedId: recoveryText(x.feed_input_id), qty: recoveryText(x.qty)})));
      setDoneState('none');
    },
    error: setErr,
    opened: () => {
      setStuckOpen(false);
      setErr('');
    },
    externallyBusy: submitting,
  });
  // The unfinished entry on this device. Restore never sends; Log Feed stays the only send.
  const draft = useAddFeedDraft({answers, paused: !!recovery.loaded, apply: restoreAnswers});
  function retainedFeed(key: 'cattleFeedsJ' | 'sheepFeedsJ', row: SheepFeedRow | CattleFeedRow) {
    const values = recovery.loaded?.payload[key];
    return Array.isArray(values)
      ? values.find(
          (x) =>
            isRecord(x) &&
            x.feed_input_id === row.feedId &&
            Number(x.qty) === Number(row.qty) &&
            (!('isCreep' in row) || !!x.is_creep === row.isCreep),
        )
      : undefined;
  }
  React.useEffect(function () {
    setDate(todayCentralISO());
  }, []);

  // Keep the locked submitter pinned to the signed-in identity.
  React.useEffect(() => {
    if (submitterLocked && teamMember !== lockedName) setTeamMember(lockedName);
  }, [submitterLocked, lockedName, teamMember]);

  // Admin config helpers. Each read walks the operator-edited blob one proved
  // hop at a time; the fields themselves stay opaque records because this form
  // only ever compares `enabled` to false, `required` to true, and renders
  // `label` with a fallback.
  var webformsList: unknown[] = isRecord(wfCfg) && Array.isArray(wfCfg.webforms) ? wfCfg.webforms : [];
  var afWf =
    webformsList.find(function (w) {
      return isRecord(w) && w.id === 'add-feed-webform';
    }) || null;
  function getField(fieldId: string): Record<string, unknown> | null {
    if (!isRecord(afWf)) return null;
    const sections: unknown[] = Array.isArray(afWf.sections) ? afWf.sections : [];
    return (
      sections
        .flatMap(function (s): unknown[] {
          return isRecord(s) && Array.isArray(s.fields) ? s.fields : [];
        })
        .filter(isRecord)
        .find(function (f) {
          return f.id === fieldId;
        }) || null
    );
  }
  function isEnabled(fieldId: string) {
    var f = getField(fieldId);
    return f ? f.enabled !== false : true;
  }
  function isRequired(fieldId: string) {
    var f = getField(fieldId);
    return f ? f.required === true : false;
  }
  function getLabel(fieldId: string, fallback: string): string {
    var f = getField(fieldId);
    const label = f ? f.label : null;
    return label ? String(label) : fallback;
  }
  function allowAddGroup() {
    const allowMap = payloadMember(wfSettings, 'allowAddGroup');
    if (isRecord(allowMap) && 'add-feed-webform' in allowMap) return allowMap['add-feed-webform'] === true;
    return isRecord(afWf) ? afWf.allowAddGroup === true : false;
  }
  var reqStar = function (fieldId: string) {
    return isRequired(fieldId)
      ? React.createElement('span', {style: {color: 'var(--danger-ink)', marginLeft: 2}}, '*')
      : null;
  };

  function getBatchOptions(): unknown[] {
    if (program === 'broiler') return broilerGroups;
    if (program === 'pig') return pigGroups;
    if (program === 'layer') return layerGroupNames;
    return [];
  }
  // NOTE (pre-existing, unchanged here): nothing in this file calls
  // buildRecord. The submit path goes through buildSubmitPayload and the RPC
  // registry; this is dead code left over from the pre-mig-034 direct-insert
  // form. Typed rather than removed — deleting it is a separate decision.
  function buildRecord(bl: string, ft: string, fl: string) {
    var id = String(Date.now()) + Math.random().toString(36).slice(2, 6);
    var now = new Date().toISOString();
    var lbs = parseFloat(fl) || 0;
    if (program === 'layer')
      return {
        id: id,
        submitted_at: now,
        date: date,
        team_member: teamMember || null,
        batch_label: bl,
        batch_id: layerBatchIdMap[bl] || null,
        feed_lbs: lbs,
        feed_type: ft || null,
        source: 'add_feed_webform',
      };
    if (program === 'broiler')
      return {
        id: id,
        submitted_at: now,
        date: date,
        team_member: teamMember || null,
        batch_label: bl,
        feed_lbs: lbs,
        feed_type: ft || null,
        source: 'add_feed_webform',
      };
    return {
      id: id,
      submitted_at: now,
      date: date,
      team_member: teamMember || null,
      batch_label: bl,
      batch_id: bl.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      feed_lbs: lbs,
      source: 'add_feed_webform',
    };
  }
  // Build a normalized payload from the relevant program branch's form
  // state. Pure — no side effects; the RPC registry consumes this and
  // the hook generates stable csid + parentId for retry determinism.
  function buildSubmitPayload() {
    if (program === 'sheep') {
      const filled = (sheepRows || []).filter((r) => r.feedId && r.qty !== '' && r.qty != null);
      const sFeedsJ = filled
        .map((r) => {
          const retained = retainedFeed('sheepFeedsJ', r);
          if (retained) return retained;
          const fi = cattleFeedInputs.find((x) => x.id === r.feedId);
          if (!fi) return null;
          const qty = parseFloat(r.qty) || 0;
          const unitWt = parseFloat(String(fi.unit_weight_lbs)) || 1;
          return {
            feed_input_id: fi.id,
            feed_name: fi.name,
            category: fi.category,
            qty,
            unit: fi.unit,
            lbs_as_fed: Math.round(qty * unitWt * 100) / 100,
            is_creep: false,
          };
        })
        .filter(Boolean);
      return {
        program: 'sheep',
        date,
        team_member: teamMember || null,
        sheepFlock,
        sheepFeedsJ: sFeedsJ,
      };
    }
    if (program === 'cattle') {
      const filled = (cattleRows || []).filter((r) => r.feedId && r.qty !== '' && r.qty != null);
      const feedsJ = filled
        .map((r) => {
          const retained = retainedFeed('cattleFeedsJ', r);
          if (retained) return retained;
          const fi = cattleFeedInputs.find((x) => x.id === r.feedId);
          if (!fi) return null;
          const qty = parseFloat(r.qty) || 0;
          const unitWt = parseFloat(String(fi.unit_weight_lbs)) || 1;
          return {
            feed_input_id: fi.id,
            feed_name: fi.name,
            category: fi.category,
            qty,
            unit: fi.unit,
            lbs_as_fed: Math.round(qty * unitWt * 100) / 100,
            is_creep: !!r.isCreep,
            nutrition_snapshot: {
              moisture_pct: fi.moisture_pct,
              nfc_pct: fi.nfc_pct,
              protein_pct: fi.protein_pct,
            },
          };
        })
        .filter(Boolean);
      return {
        program: 'cattle',
        date,
        team_member: teamMember || null,
        cattleHerd,
        cattleFeedsJ: feedsJ,
      };
    }
    // broiler / pig / layer — multi-row form. Layer's batch_id needs to
    // resolve from layerBatchIdMap before the registry gets the payload
    // (the registry doesn't know about the map; resolution is a UI concern).
    // A recovered layer group keeps the batch it was logged against; the
    // current housing map only resolves groups the recovery did not carry.
    const recoveredBatchIds = new Map<string, unknown>();
    const recovered = recovery.loaded?.payload;
    if (recovered && recovered.program === 'layer') {
      const groups = [
        recovered,
        ...(Array.isArray(recovered.extraGroups) ? recovered.extraGroups.filter(isRecord) : []),
      ];
      for (const g of groups) {
        if (typeof g.batchLabel === 'string' && g.batchId != null) recoveredBatchIds.set(g.batchLabel, g.batchId);
      }
    }
    const resolveBatchId = (label: string) =>
      program === 'layer' ? (recoveredBatchIds.get(label) ?? (layerBatchIdMap[label] || null)) : null;
    return {
      program,
      date,
      team_member: teamMember || null,
      batchLabel,
      feedType,
      feedLbs,
      batchId: resolveBatchId(batchLabel),
      extraGroups: (extraGroups || [])
        .filter((g) => g.batchLabel)
        .map((g) => ({
          batchLabel: g.batchLabel,
          feedType: g.feedType,
          feedLbs: g.feedLbs,
          batchId: resolveBatchId(g.batchLabel),
        })),
    };
  }

  async function handleSubmit() {
    if (!date) {
      setErr('Please enter a date.');
      return;
    }
    if (isRequired('team_member') && !teamMember) {
      setErr(getLabel('team_member', 'Team member') + ' is required.');
      return;
    }
    // Per-program validation. None of these touch DB; they all return
    // before the queue submit so a bad form doesn't queue.
    if (program === 'sheep') {
      if (!sheepFlock) {
        setErr('Please select a flock.');
        return;
      }
      const sFilled = (sheepRows || []).filter((r) => r.feedId && r.qty !== '' && r.qty != null);
      if (sFilled.length === 0) {
        setErr('Please enter at least one feed.');
        return;
      }
    } else if (program === 'cattle') {
      if (!cattleHerd) {
        setErr('Please select a herd.');
        return;
      }
      const cFilled = (cattleRows || []).filter((r) => r.feedId && r.qty !== '' && r.qty != null);
      if (cFilled.length === 0) {
        setErr('Please enter at least one feed.');
        return;
      }
    } else {
      // broiler / pig / layer
      if (!batchLabel) {
        setErr('Please select a batch/group.');
        return;
      }
      if (isRequired('feed_lbs') && (!feedLbs || parseFloat(feedLbs) <= 0)) {
        setErr(getLabel('feed_lbs', 'Feed (lbs)') + ' is required.');
        return;
      }
      if (
        program !== 'pig' &&
        isEnabled('feed_type') &&
        isRequired('feed_type') &&
        parseFloat(feedLbs) > 0 &&
        !feedType
      ) {
        setErr(getLabel('feed_type', 'Feed Type') + ' is required when feed is entered.');
        return;
      }
      for (let i = 0; i < extraGroups.length; i++) {
        const eg = extraGroups[i];
        if (!eg.batchLabel) continue;
        if (isRequired('feed_lbs') && (!eg.feedLbs || parseFloat(eg.feedLbs) <= 0)) {
          setErr(getLabel('feed_lbs', 'Feed (lbs)') + ' is required for ' + eg.batchLabel + '.');
          return;
        }
        if (
          program !== 'pig' &&
          isEnabled('feed_type') &&
          isRequired('feed_type') &&
          parseFloat(eg.feedLbs) > 0 &&
          !eg.feedType
        ) {
          setErr(getLabel('feed_type', 'Feed Type') + ' is required for ' + eg.batchLabel + ' when feed is entered.');
          return;
        }
      }
    }

    if (!(await recovery.beginSubmit())) return;
    setErr('');
    setSubmitting(true);
    try {
      const payload = buildSubmitPayload();
      const csid = await draft.beforeSend();
      const result = await submit(payload, csid ? {...recovery.options, csid} : recovery.options);
      if (!recovery.finish(true)) {
        // Synced, or held by the queue for its own retry: the draft is spent,
        // and is cleared (bounded) before the result shows.
        await draft.afterSend(true);
        setDoneState(result.state);
      }
    } catch (e) {
      // Schema/validation error — useOfflineRpcSubmit throws on PGRST/22*/23*.
      // Surface to operator instead of silently queuing.
      setErr(withSavePrefix(errorMessage(e)));
    } finally {
      recovery.finish();
      setSubmitting(false);
      // Not confirmed: the draft stays (a no-op once a confirmed send settled it).
      void draft.afterSend(false);
    }
  }

  function resetForm() {
    setBatchLabel('');
    setFeedType('');
    setFeedLbs('');
    setExtraGroups([]);
    setCattleHerd('');
    setCattleRows([{feedId: '', qty: '', isCreep: false}]);
    setSheepFlock('');
    setSheepRows([{feedId: '', qty: ''}]);
    setErr('');
    setDoneState('none');
  }

  var wfBg: React.CSSProperties = {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    padding: '1rem',
    fontFamily: 'inherit',
  };
  var cardS: React.CSSProperties = {
    background: 'white',
    borderRadius: 18,
    padding: '20px',
    marginBottom: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,.08)',
  };
  var inpS: React.CSSProperties = {
    fontFamily: 'inherit',
    fontSize: 15,
    padding: '10px 12px',
    border: '1px solid var(--border-strong)',
    borderRadius: 12,
    width: '100%',
    outline: 'none',
    background: 'white',
    color: 'var(--ink)',
    boxSizing: 'border-box',
  };
  var lblS: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    color: 'var(--ink)',
    marginBottom: 5,
    fontWeight: 600,
  };
  var logoEl = (
    <div style={{textAlign: 'center', marginBottom: 20}}>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: 'var(--brand)',
          letterSpacing: -0.3,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <PlannerIcon iconKey="feed" size={22} />
        <span>WCF Planner</span>
      </div>
      <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>Quick Feed Log</div>
    </div>
  );

  if (doneState !== 'none')
    return (
      <div style={wfBg}>
        {recovery.banner}
        <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '2rem', textAlign: 'center'}}>
          {logoEl}
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: doneState === 'queued' ? 'var(--warn-ink)' : 'var(--ink)',
              marginBottom: 8,
            }}
          >
            {doneState === 'queued' ? 'Saved on this device' : 'Feed logged!'}
          </div>
          {doneState === 'queued' && (
            <div
              data-submit-state="queued"
              style={{
                fontSize: 12,
                color: 'var(--text-2)',
                marginBottom: 12,
                lineHeight: 1.5,
                background: 'var(--warn-soft)',
                border: '1px solid var(--warn-stripe)',
                borderRadius: 12,
                padding: '8px 12px',
                textAlign: 'left',
              }}
            >
              No connection right now. Your entry is queued and will sync as soon as the device is back online. Keep
              logging \u2014 additional entries will queue too.
            </div>
          )}
          {doneState === 'synced' && (
            <div data-submit-state="synced" style={{display: 'none'}}>
              synced
            </div>
          )}
          <div style={{fontSize: 14, color: 'var(--ink-muted)', marginBottom: 28, lineHeight: 1.6}}>
            {program === 'cattle'
              ? cattleHerd +
                ' \u2014 ' +
                cattleRows.filter(function (r) {
                  return r.feedId && r.qty;
                }).length +
                ' feed entr' +
                (cattleRows.filter(function (r) {
                  return r.feedId && r.qty;
                }).length === 1
                  ? 'y'
                  : 'ies')
              : program === 'sheep'
                ? sheepFlock +
                  ' \u2014 ' +
                  sheepRows.filter(function (r) {
                    return r.feedId && r.qty;
                  }).length +
                  ' feed entr' +
                  (sheepRows.filter(function (r) {
                    return r.feedId && r.qty;
                  }).length === 1
                    ? 'y'
                    : 'ies')
                : batchLabel +
                  ' \u2014 ' +
                  feedLbs +
                  ' lbs' +
                  (feedType ? ' (' + feedType + ')' : '') +
                  (extraGroups.filter(function (g) {
                    return g.batchLabel;
                  }).length > 0
                    ? ' + ' +
                      extraGroups.filter(function (g) {
                        return g.batchLabel;
                      }).length +
                      ' more'
                    : '')}
          </div>
          <button
            onClick={resetForm}
            className="wcf-clickable wcf-btn wcf-btn--primary"
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 999,
              border: 'none',
              background: 'var(--brand)',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 10,
            }}
          >
            Log Another
          </button>
          <button
            onClick={function () {
              window.location.hash = '#webforms';
              window.location.reload();
            }}
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 999,
              border: '1px solid var(--border-strong)',
              background: 'white',
              color: 'var(--ink)',
              fontSize: 15,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
            className="wcf-clickable wcf-btn wcf-btn--secondary"
          >
            Done
          </button>
        </div>
      </div>
    );

  // Render a feed group section (batch + feed type + lbs) — used for main and extras
  function renderFeedGroup(
    bl: string,
    ft: string,
    fl: string,
    onChange: (key: keyof ExtraFeedGroup, value: string) => void,
    idx: number | null,
  ) {
    var batchOpts = getBatchOptions();
    return React.createElement(
      'div',
      {key: idx != null ? 'eg' + idx : 'main'},
      React.createElement(
        'div',
        {style: cardS},
        idx != null &&
          React.createElement(
            'div',
            {style: {display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8}},
            React.createElement(
              'div',
              {style: {fontSize: 13, fontWeight: 600, color: 'var(--brand)'}},
              getLabel('batch_label', 'Batch / Group') + ' ' + (idx + 2),
            ),
            React.createElement(
              'button',
              {
                type: 'button',
                onClick: function () {
                  setExtraGroups(function (p) {
                    return p.filter(function (_, i) {
                      return i !== idx;
                    });
                  });
                },
                style: {background: 'none', border: 'none', color: 'var(--ink-faint)', cursor: 'pointer', fontSize: 18},
              },
              '\u00d7',
            ),
          ),
        idx == null &&
          React.createElement(
            'label',
            {style: lblS},
            getLabel(
              'batch_label',
              program === 'pig' ? 'Pig Group' : program === 'broiler' ? 'Broiler Batch' : 'Layer Group',
            ),
            reqStar('batch_label'),
          ),
        React.createElement(
          'select',
          {
            value: bl,
            onChange: function (e: React.ChangeEvent<HTMLSelectElement>) {
              onChange('batchLabel', e.target.value);
            },
            style: inpS,
          },
          React.createElement('option', {value: ''}, 'Select...'),
          batchOpts.map(function (b) {
            var name = batchOptionName(b);
            var label = program === 'broiler' ? formatBroilerBatchLabel(name, broilerMeta) : name;
            return React.createElement('option', {key: name, value: name}, label);
          }),
        ),
        program === 'layer' &&
          bl &&
          housingBatchMap[bl] &&
          React.createElement(
            'div',
            {
              style: {
                marginTop: 6,
                fontSize: 11,
                color: 'var(--info-ink)',
                background: 'var(--info-soft)',
                borderRadius: 10,
                padding: '4px 8px',
              },
            },
            'Active in batch: ',
            React.createElement('strong', null, housingBatchMap[bl]),
          ),
      ),
      program !== 'pig' &&
        isEnabled('feed_type') &&
        React.createElement(
          'div',
          {style: cardS},
          React.createElement(
            'label',
            {style: lblS},
            getLabel('feed_type', 'Feed Type'),
            idx == null && reqStar('feed_type'),
          ),
          React.createElement(
            'div',
            {style: {display: 'flex', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-strong)'}},
            (program === 'layer' ? ['STARTER', 'GROWER', 'LAYER'] : ['STARTER', 'GROWER']).map(function (f, fi, arr) {
              return React.createElement(
                'button',
                {
                  key: f,
                  type: 'button',
                  onClick: function () {
                    onChange('feedType', ft === f ? '' : f);
                  },
                  style: {
                    flex: 1,
                    padding: '10px 0',
                    border: ft === f ? '1px solid var(--brand)' : '1px solid var(--border-strong)',
                    borderRight: fi < arr.length - 1 ? '1px solid var(--border-strong)' : 'none',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: 'white',
                    color: ft === f ? 'var(--brand)' : 'var(--ink-muted)',
                  },
                },
                f,
              );
            }),
          ),
        ),
      isEnabled('feed_lbs') &&
        React.createElement(
          'div',
          {style: cardS},
          React.createElement(
            'label',
            {style: lblS},
            getLabel('feed_lbs', 'Feed (lbs)'),
            idx == null && reqStar('feed_lbs'),
          ),
          React.createElement('input', {
            type: 'number',
            min: '0',
            step: '0.1',
            value: fl,
            onChange: function (e: React.ChangeEvent<HTMLInputElement>) {
              onChange('feedLbs', e.target.value);
            },
            placeholder: '0',
            style: inpS,
          }),
        ),
    );
  }

  return (
    <div style={wfBg}>
      {recovery.banner}
      <div style={{maxWidth: 480, margin: '0 auto', paddingTop: '1rem'}}>
        {logoEl}
        {draft.view}

        {stuckRows.length > 0 && (
          <button
            onClick={() => setStuckOpen(true)}
            data-stuck-button="1"
            style={{
              width: '100%',
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid var(--warn-stripe)',
              background: 'var(--warn-soft)',
              color: 'var(--warn-ink)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
              textAlign: 'left',
            }}
          >
            {stuckRows.length} unsynced Add Feed submission{stuckRows.length === 1 ? '' : 's'} — tap to review
          </button>
        )}

        <div
          style={{
            background: 'var(--ok-soft)',
            borderRadius: 10,
            fontSize: 12,
            color: 'var(--brand)',
            padding: '10px 16px',
            marginBottom: 16,
          }}
        >
          For quick feed logging only. For full daily reports including mortality and care checks, use the Daily Report
          forms.
        </div>

        <div style={cardS}>
          <label style={lblS}>
            {getLabel('date', 'Date')}
            {reqStar('date')}
          </label>
          <input
            type="date"
            value={date}
            onChange={function (e) {
              setDate(e.target.value);
            }}
            style={inpS}
          />
        </div>

        <div style={cardS}>
          <LockedSubmitter name={lockedName} label={getLabel('team_member', 'Team member')} labelStyle={lblS} />
        </div>

        <div style={cardS}>
          <label style={lblS}>Program</label>
          <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: 6}}>
            {[
              {key: 'pig', iconKey: ANIMAL_ICON_KEYS.pig, label: 'Pig'},
              {key: 'broiler', iconKey: ANIMAL_ICON_KEYS.broiler, label: 'Broiler'},
              {key: 'layer', iconKey: ANIMAL_ICON_KEYS.layer, label: 'Layer'},
              {key: 'cattle', iconKey: ANIMAL_ICON_KEYS.cattle, label: 'Cattle'},
              {key: 'sheep', iconKey: ANIMAL_ICON_KEYS.sheep, label: 'Sheep'},
            ].map(function (p) {
              // F048: species identity in a picker = small program dot + black
              // label, NOT colored border/text. Selection is shown with a neutral
              // stronger border + tinted neutral fill, no per-program palette.
              var selected = program === p.key;
              return (
                <button
                  key={p.key}
                  onClick={function () {
                    setProgram(p.key);
                    setBatchLabel('');
                    setFeedType('');
                    setExtraGroups([]);
                    setCattleHerd('');
                    setCattleRows([{feedId: '', qty: '', isCreep: false}]);
                    setSheepFlock('');
                    setSheepRows([{feedId: '', qty: ''}]);
                  }}
                  style={{
                    padding: '10px 4px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    border: selected ? '2px solid var(--border-strong)' : '2px solid var(--border)',
                    background: selected ? 'var(--surface-2)' : 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <PlannerIcon iconKey={p.iconKey} size={24} />
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                    }}
                  >
                    <span style={programDotStyle(p.key, 7)} />
                    {p.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {program &&
          program !== 'cattle' &&
          program !== 'sheep' &&
          renderFeedGroup(
            batchLabel,
            feedType,
            feedLbs,
            function (k, v) {
              if (k === 'batchLabel') setBatchLabel(v);
              else if (k === 'feedType') setFeedType(v);
              else if (k === 'feedLbs') setFeedLbs(v);
            },
            null,
          )}

        {program &&
          program !== 'cattle' &&
          program !== 'sheep' &&
          extraGroups.map(function (eg, ei) {
            return renderFeedGroup(
              eg.batchLabel || '',
              eg.feedType || '',
              eg.feedLbs || '',
              function (k, v) {
                setExtraGroups(function (p) {
                  return p.map(function (g, i) {
                    if (i !== ei) return g;
                    // Same shallow copy the `for (var x in g)` loop made, with the
                    // one edited member written after it.
                    var n = {...g};
                    n[k] = v;
                    return n;
                  });
                });
              },
              ei,
            );
          })}

        {program && program !== 'cattle' && program !== 'sheep' && allowAddGroup() && (
          <button
            type="button"
            onClick={function () {
              setExtraGroups(function (p) {
                return p.concat([{batchLabel: '', feedType: '', feedLbs: ''}]);
              });
            }}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 12,
              border: '2px dashed var(--border)',
              background: 'transparent',
              color: 'var(--brand)',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 12,
            }}
          >
            + Add Another Group
          </button>
        )}

        {program === 'cattle' && (
          <React.Fragment>
            {/* Herd selector */}
            <div style={cardS}>
              <label style={lblS}>
                Herd<span style={{color: 'var(--danger-ink)', marginLeft: 2}}>*</span>
              </label>
              <select
                value={cattleHerd}
                onChange={function (e) {
                  setCattleHerd(e.target.value);
                  setCattleRows([{feedId: '', qty: '', isCreep: false}]);
                }}
                style={inpS}
              >
                <option value="">Select herd...</option>
                <option value="mommas">Mommas</option>
                <option value="backgrounders">Backgrounders</option>
                <option value="finishers">Finishers</option>
                <option value="bulls">Bulls</option>
              </select>
            </div>

            {/* Feed rows — one per feed */}
            {cattleHerd &&
              (function () {
                var feedsForHerd = cattleFeedInputs.filter(function (f) {
                  return (f.herd_scope || []).includes(cattleHerd);
                });
                var showCreep = cattleHerd === 'mommas';
                return cattleRows.map(function (row, ri) {
                  var fi = cattleFeedInputs.find(function (x) {
                    return x.id === row.feedId;
                  });
                  return React.createElement(
                    'div',
                    {key: ri, style: cardS},
                    cattleRows.length > 1 &&
                      React.createElement(
                        'div',
                        {
                          style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          },
                        },
                        React.createElement(
                          'div',
                          {style: {fontSize: 13, fontWeight: 600, color: 'var(--ink)'}},
                          'Feed ' + (ri + 1),
                        ),
                        React.createElement(
                          'button',
                          {
                            type: 'button',
                            onClick: function () {
                              setCattleRows(
                                cattleRows.filter(function (_, i) {
                                  return i !== ri;
                                }),
                              );
                            },
                            style: {
                              background: 'none',
                              border: 'none',
                              color: 'var(--ink-faint)',
                              cursor: 'pointer',
                              fontSize: 18,
                            },
                          },
                          '\u00d7',
                        ),
                      ),
                    React.createElement('label', {style: lblS}, 'Feed Type'),
                    React.createElement(
                      'select',
                      {
                        value: row.feedId,
                        onChange: function (e: React.ChangeEvent<HTMLSelectElement>) {
                          setCattleRows(
                            cattleRows.map(function (r, i) {
                              return i === ri ? Object.assign({}, r, {feedId: e.target.value}) : r;
                            }),
                          );
                        },
                        style: inpS,
                      },
                      React.createElement('option', {value: ''}, 'Select feed...'),
                      feedsForHerd.map(function (f) {
                        return React.createElement('option', {key: f.id, value: f.id}, f.name);
                      }),
                    ),
                    React.createElement(
                      'div',
                      {style: {marginTop: 10}},
                      React.createElement('label', {style: lblS}, fi ? 'Qty (' + fi.unit + ')' : 'Qty'),
                      React.createElement('input', {
                        type: 'number',
                        min: '0',
                        step: '0.1',
                        value: row.qty,
                        onChange: function (e: React.ChangeEvent<HTMLInputElement>) {
                          setCattleRows(
                            cattleRows.map(function (r, i) {
                              return i === ri ? Object.assign({}, r, {qty: e.target.value}) : r;
                            }),
                          );
                        },
                        placeholder: '0',
                        style: inpS,
                      }),
                    ),
                    showCreep &&
                      row.feedId &&
                      React.createElement(
                        'label',
                        {
                          style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                            fontSize: 11,
                            color: 'var(--danger-ink)',
                            cursor: 'pointer',
                            marginTop: 8,
                            userSelect: 'none',
                          },
                        },
                        React.createElement('input', {
                          type: 'checkbox',
                          checked: !!row.isCreep,
                          onChange: function (e: React.ChangeEvent<HTMLInputElement>) {
                            setCattleRows(
                              cattleRows.map(function (r, i) {
                                return i === ri ? Object.assign({}, r, {isCreep: e.target.checked}) : r;
                              }),
                            );
                          },
                          style: {cursor: 'pointer'},
                        }),
                        'This was creep feed (for calves)',
                      ),
                  );
                });
              })()}

            {cattleHerd && (
              <button
                type="button"
                onClick={function () {
                  setCattleRows(cattleRows.concat([{feedId: '', qty: '', isCreep: false}]));
                }}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 12,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  // WI-2d: secondary add-row action — consistent brand ink, not a
                  // per-program color.
                  color: 'var(--brand)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 12,
                }}
              >
                + Add Feed
              </button>
            )}
          </React.Fragment>
        )}

        {program === 'sheep' && (
          <React.Fragment>
            {/* Flock selector */}
            <div style={cardS}>
              <label style={lblS}>
                Flock<span style={{color: 'var(--danger-ink)', marginLeft: 2}}>*</span>
              </label>
              <select
                value={sheepFlock}
                onChange={function (e) {
                  setSheepFlock(e.target.value);
                  setSheepRows([{feedId: '', qty: ''}]);
                }}
                style={inpS}
              >
                <option value="">Select flock...</option>
                <option value="rams">Rams</option>
                <option value="ewes">Ewes</option>
                <option value="feeders">Feeders</option>
              </select>
            </div>
            {/* Feed rows — one per feed, sheep-scoped feed master list */}
            {sheepFlock &&
              (function () {
                var feedsForFlock = cattleFeedInputs.filter(function (f) {
                  return f.category !== 'mineral' && (f.herd_scope || []).includes(sheepFlock);
                });
                return sheepRows.map(function (row, ri) {
                  var fi = cattleFeedInputs.find(function (x) {
                    return x.id === row.feedId;
                  });
                  return React.createElement(
                    'div',
                    {key: ri, style: cardS},
                    sheepRows.length > 1 &&
                      React.createElement(
                        'div',
                        {
                          style: {
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: 8,
                          },
                        },
                        React.createElement(
                          'div',
                          {style: {fontSize: 13, fontWeight: 600, color: 'var(--ink)'}},
                          'Feed ' + (ri + 1),
                        ),
                        React.createElement(
                          'button',
                          {
                            type: 'button',
                            onClick: function () {
                              setSheepRows(
                                sheepRows.filter(function (_, i) {
                                  return i !== ri;
                                }),
                              );
                            },
                            style: {
                              background: 'none',
                              border: 'none',
                              color: 'var(--ink-faint)',
                              cursor: 'pointer',
                              fontSize: 18,
                            },
                          },
                          '×',
                        ),
                      ),
                    React.createElement('label', {style: lblS}, 'Feed Type'),
                    React.createElement(
                      'select',
                      {
                        value: row.feedId,
                        onChange: function (e: React.ChangeEvent<HTMLSelectElement>) {
                          setSheepRows(
                            sheepRows.map(function (r, i) {
                              return i === ri ? Object.assign({}, r, {feedId: e.target.value}) : r;
                            }),
                          );
                        },
                        style: inpS,
                      },
                      React.createElement('option', {value: ''}, 'Select feed...'),
                      feedsForFlock.map(function (f) {
                        return React.createElement('option', {key: f.id, value: f.id}, f.name);
                      }),
                    ),
                    React.createElement(
                      'div',
                      {style: {marginTop: 10}},
                      React.createElement('label', {style: lblS}, fi ? 'Qty (' + fi.unit + ')' : 'Qty'),
                      React.createElement('input', {
                        type: 'number',
                        min: '0',
                        step: '0.1',
                        value: row.qty,
                        onChange: function (e: React.ChangeEvent<HTMLInputElement>) {
                          setSheepRows(
                            sheepRows.map(function (r, i) {
                              return i === ri ? Object.assign({}, r, {qty: e.target.value}) : r;
                            }),
                          );
                        },
                        placeholder: '0',
                        style: inpS,
                      }),
                    ),
                  );
                });
              })()}
            {sheepFlock && (
              <button
                type="button"
                onClick={function () {
                  setSheepRows(sheepRows.concat([{feedId: '', qty: ''}]));
                }}
                style={{
                  width: '100%',
                  padding: 12,
                  borderRadius: 12,
                  border: '2px dashed var(--border)',
                  background: 'transparent',
                  // WI-2d: secondary add-row action — consistent brand ink, not a
                  // per-program color.
                  color: 'var(--brand)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginBottom: 12,
                }}
              >
                + Add Feed
              </button>
            )}
          </React.Fragment>
        )}

        {err && (
          <div
            style={{
              color: 'var(--danger-ink)',
              fontSize: 13,
              marginBottom: 10,
              padding: '8px 12px',
              background: 'var(--danger-soft)',
              borderRadius: 10,
            }}
          >
            {err}
          </div>
        )}

        {program && (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="wcf-clickable wcf-btn wcf-btn--primary"
            style={{
              width: '100%',
              padding: 14,
              border: 'none',
              borderRadius: 999,
              background: 'var(--brand)',
              color: 'white',
              fontSize: 15,
              fontWeight: 600,
              cursor: submitting ? 'default' : 'pointer',
              opacity: submitting ? 0.45 : 1,
              fontFamily: 'inherit',
              marginBottom: 16,
            }}
          >
            {submitting
              ? 'Submitting\u2026'
              : 1 +
                    extraGroups.filter(function (g) {
                      return g.batchLabel;
                    }).length >
                  1
                ? 'Log ' +
                  (1 +
                    extraGroups.filter(function (g) {
                      return g.batchLabel;
                    }).length) +
                  ' Feed Entries'
                : 'Log Feed'}
          </button>
        )}

        <div style={{textAlign: 'center', marginTop: 12}}>
          <button
            onClick={function () {
              window.location.hash = '#webforms';
              window.location.reload();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--brand)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
              textDecoration: 'underline',
            }}
          >
            {'\u2190 Back to Daily Reports'}
          </button>
        </div>
      </div>

      {stuckOpen && (
        <StuckSubmissionsModal
          onOpen={recovery.open}
          busy={submitting || recovery.opening}
          error={err}
          rows={stuckRows}
          formLabel="Add Feed submission"
          describeRow={(row) => {
            const args = payloadMember(row.record, 'args');
            const parent = payloadMember(args, 'parent_in');
            const children = payloadMember(args, 'children_in');
            const childCount = Array.isArray(children) ? children.length : 0;
            const prog = payloadText(parent, 'program');
            const dateStr = payloadText(parent, 'date');
            return `${prog} \u00b7 ${dateStr} \u00b7 ${childCount} entr${childCount === 1 ? 'y' : 'ies'} (not yet sent)`;
          }}
          onRetry={async (csid) => {
            await retryStuck(csid);
          }}
          onDiscard={async (csid) => {
            await discardStuck(csid);
          }}
          onClose={() => setStuckOpen(false)}
        />
      )}
    </div>
  );
};

export default AddFeedWebform;
